import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useOrganizations } from "./hooks/useOrganization";
import { useOrgsOverview } from "./hooks/useOrgsOverview";
import { PeriodPicker, PeriodPreset, periodFor } from "@/components/PeriodPicker";
import { KpiPeriod } from "@/models/org-kpis";
import { formatters } from "@/utils/formatters";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { LoadingState } from "@/components/ui/loading-state";
import { EmptyState } from "@/components/ui/empty-state";
import { Button } from "@/components/ui/button";
import { Building2, Mail, Phone, Globe, Plus, Wallet, ArrowUpRight } from "lucide-react";
import { format } from "date-fns";
import { CreateOrganizationDialog } from "./components/CreateOrganizationDialog";
import { AdjustWalletDialog } from "./components/AdjustWalletDialog";

const OrganizationsPage = () => {
  const { data: organizations, isLoading, error, refetch } = useOrganizations();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isWalletDialogOpen, setIsWalletDialogOpen] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [preset, setPreset] = useState<PeriodPreset>("last_30_days");
  const [period, setPeriod] = useState<KpiPeriod>(() => periodFor("last_30_days"));
  const { data: overview, isLoading: isOverviewLoading } = useOrgsOverview(period);
  const navigate = useNavigate();

  // Usage per organization, by _id. Absent while loading or for a brand-new org.
  const usageById = useMemo(
    () => new Map((overview ?? []).map((row) => [row.organizationId, row])),
    [overview]
  );
  // Highest new-pricing cost first, so the orgs most affected by the change lead.
  const sortedOrganizations = useMemo(
    () =>
      [...(organizations ?? [])].sort(
        (a, b) => (usageById.get(b._id)?.costNew ?? 0) - (usageById.get(a._id)?.costNew ?? 0)
      ),
    [organizations, usageById]
  );

  const handleCreateOrganization = () => {
    setIsDialogOpen(true);
  };

  const handleAdjustWallet = (id: string) => {
    setSelectedOrgId(id);
    setIsWalletDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Organizations"
        description="Usage and estimated Meta cost per organization. Open one for the full breakdown."
        action={
          <Button onClick={handleCreateOrganization} className="gap-2">
            <Plus className="h-4 w-4" />
            Create Organization
          </Button>
        }
      />

      <PeriodPicker
        preset={preset}
        period={period}
        onChange={(next, nextPeriod) => {
          setPreset(next);
          setPeriod(nextPeriod);
        }}
      />

      <CreateOrganizationDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} />

      {selectedOrgId && (
        <AdjustWalletDialog
          open={isWalletDialogOpen}
          onOpenChange={setIsWalletDialogOpen}
          organizationId={selectedOrgId}
        />
      )}

      {isLoading ? (
        <LoadingState isLoading={isLoading} loadingText="Loading organizations..." />
      ) : error ? (
        <LoadingState
          error={error}
          errorTitle="Failed to load organizations"
          errorDescription="There was an error loading the organizations. Please try again."
          onRetry={() => refetch()}
        />
      ) : !organizations || organizations.length === 0 ? (
        <EmptyState
          icon={Building2}
          title="No organizations found"
          description="There are no organizations in the system yet."
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organization</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Website</TableHead>
                  <TableHead className="text-right">Messages</TableHead>
                  <TableHead className="text-right">Meta cost new (then)</TableHead>
                  <TableHead>Last active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedOrganizations.map((organization) => {
                  const usage = usageById.get(organization._id);
                  return (
                    <TableRow
                      key={organization._id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/organizations/${organization._id}`)}
                    >
                      <TableCell>
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-primary/10 border border-primary/20">
                            <Building2 className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <div className="font-medium text-foreground">{organization.name}</div>
                            <div className="text-sm text-muted-foreground">
                              ID: {organization.orgId}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {organization.email && (
                            <div className="flex items-center gap-2 text-sm">
                              <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="text-muted-foreground">{organization.email}</span>
                            </div>
                          )}
                          {organization.phone && (
                            <div className="flex items-center gap-2 text-sm">
                              <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                              <span className="text-muted-foreground">{organization.phone}</span>
                            </div>
                          )}
                          {!organization.email && !organization.phone && (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {organization.website ? (
                          <a
                            href={organization.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-sm text-primary hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Globe className="h-3.5 w-3.5" />
                            {organization.website}
                          </a>
                        ) : (
                          <span className="text-sm text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {usage ? (
                          <>
                            <div>{formatters.formatNumber(usage.sent + usage.received)}</div>
                            <div className="text-xs text-muted-foreground">
                              {formatters.formatNumber(usage.sent)} sent
                            </div>
                          </>
                        ) : (
                          <span className="text-muted-foreground">
                            {isOverviewLoading ? "…" : "—"}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {usage ? (
                          <>
                            <div className="font-medium">
                              {formatters.formatCurrency(usage.costNew)}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              was {formatters.formatCurrency(usage.costThen)}
                            </div>
                          </>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {usage?.lastActiveDay ? (
                          <span className="text-sm text-muted-foreground">
                            {format(new Date(usage.lastActiveDay), "MMM d, yyyy")}
                          </span>
                        ) : (
                          <Badge variant="outline">No traffic</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAdjustWallet(organization._id);
                            }}
                            title="Adjust Wallet"
                          >
                            <Wallet className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" title="Open usage">
                            <ArrowUpRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default OrganizationsPage;
